import { AnyAction } from 'redux';
import {
  GlobalActions,
  TiergruppeAnimal,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import { FamilienStand, Tarifbereich, ThhTier, Tierart, Tiergroesse } from '@ps20/bff-models';
import {
  App,
  determineAssekuranztarifFromTarifbereich,
  ThhPferdVorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { StockmassMehrerePferdeChangedPayload, ThhPferdActions } from '../thhPferdActions';

export enum AnzahlTiereEnum {
  EINES = 'EIN_TIER',
  MEHRERE = 'MEHRERE_TIERE',
}

export enum Stockmass {
  KLEINER = 'KLEINER',
  GROESSER = 'GROESSER',
}

export const initialAnzahlMehrerePferde = 2;
export const maximalAnzahlPferdeOhneUnderwriter = 10;

export interface Versicherungsnehmer {
  geburtsdatum: string;
  familienstand: FamilienStand;
  isAssekuranztarif: boolean;
  tarifbereich?: Tarifbereich;
  anzahlTiere: AnzahlTiereEnum;
  stockmass: Stockmass;
  stockmassMehrerePferde: Array<Stockmass>;
  anzahlMehrerePferde: number;
  tiere?: ThhTier[];
  tiergruppeAnimals?: TiergruppeAnimal[];
}

export const versicherungsnehmerReducer = function (
  sectionState: Versicherungsnehmer = null,
  action: AnyAction,
): Versicherungsnehmer {
  switch (action.type) {
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      if (action.payload.versicherungsnehmer?.geburtsdatum) {
        const dateSplitted = action.payload.versicherungsnehmer.geburtsdatum.split('-');
        const dateAsString = `${dateSplitted[2]}.${dateSplitted[1]}.${dateSplitted[0]}`;
        return {
          ...sectionState,
          geburtsdatum: dateAsString,
        };
      } else {
        return sectionState;
      }
    }
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA_AFTER_VV_LOAD:
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA: {
      return {
        ...sectionState,
        isAssekuranztarif: action.payload,
      };
    }
    case GlobalActions.TARIFBEREICH_CHANGED: {
      return {
        ...sectionState,
        tarifbereich: action.payload,
        isAssekuranztarif: determineAssekuranztarifFromTarifbereich(action.payload),
      };
    }
    case GlobalActions.THH_PFERD_VORBELEGUNG_FINISHED: {
      const actionPayload: VorbelegungPayload = action.payload;
      if (actionPayload.appId === App.THH_PFERD) {
        const vorhandeneVorbelegungdaten: ThhPferdVorbelegungdaten =
          action.payload.vorhandeneVorbelegungdaten;
        return {
          ...sectionState,
          tiere: vorhandeneVorbelegungdaten.tiere,
        };
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: ThhPferdVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.THH_PFERD) {
        return {
          ...sectionState,
          tiere: actionPayload.tiere,
        };
      }
      return sectionState;
    }
    case ThhPferdActions.GEBURTSDATUM_CHANGED: {
      const geburtsdatum = action.payload;
      return { ...sectionState, geburtsdatum };
    }
    case ThhPferdActions.ANZAHL_TIERE_CHANGED: {
      const anzahlTiere: AnzahlTiereEnum = action.payload;
      if (anzahlTiere === AnzahlTiereEnum.EINES) {
        return {
          ...sectionState,
          anzahlTiere,
          anzahlMehrerePferde: initialAnzahlMehrerePferde,
          stockmassMehrerePferde: new Array(initialAnzahlMehrerePferde),
        };
      }
      return { ...sectionState, anzahlTiere, stockmass: null };
    }
    case ThhPferdActions.STOCKMASS_CHANGED: {
      const stockmass: Stockmass = action.payload;
      return { ...sectionState, stockmass };
    }
    case ThhPferdActions.ANZAHL_MEHRERE_PFERDE_CHANGED: {
      const anzahlMehrerePferde: number = action.payload;
      const stockmassMehrerePferde = [...sectionState.stockmassMehrerePferde];
      stockmassMehrerePferde.length = Math.min(
        anzahlMehrerePferde,
        maximalAnzahlPferdeOhneUnderwriter,
      );
      return { ...sectionState, anzahlMehrerePferde, stockmassMehrerePferde, stockmass: null };
    }
    case ThhPferdActions.STOCKMASS_MEHRERE_PFERDE_CHANGED: {
      const newStockmassMehrerePferdeEntry: StockmassMehrerePferdeChangedPayload = action.payload;
      const newStockmassMehrerePferde = [...sectionState.stockmassMehrerePferde];
      newStockmassMehrerePferde[newStockmassMehrerePferdeEntry.index] =
        newStockmassMehrerePferdeEntry.stockmass;
      if (!sectionState.tiere) {
        sectionState.tiere = [];
        newStockmassMehrerePferde.forEach(() =>
          sectionState.tiere.push({ tierart: Tierart.VRPFERD } as ThhTier),
        );
      }
      return {
        ...sectionState,
        stockmassMehrerePferde: newStockmassMehrerePferde,
        tiere: sectionState.tiere?.map((tier, index) => {
          if (index === newStockmassMehrerePferdeEntry.index) {
            tier.tiergroesse = convertStockmassToTiergroesse(
              newStockmassMehrerePferdeEntry.stockmass,
            );
          }
          return tier;
        }),
      };
    }
    default:
      return sectionState;
  }
};

const convertStockmassToTiergroesse = (stockmass: Stockmass): Tiergroesse => {
  return (
    {
      [Stockmass.GROESSER]: Tiergroesse.GROSSPFERD,
      [Stockmass.KLEINER]: Tiergroesse.KLEINPFERD,
    }[stockmass] ?? Tiergroesse.UNBEKANNT
  );
};
